import React, { useCallback, useEffect, useState } from 'react';
import { SkillTag, skillTagLabel } from '../../../../services/skillFrameworkService';
import { useLessonContext } from '../../LessonContext';
import { courseRunsService } from '../../../../services/courseRunsService';
import { useLessonBatchExecution } from '../../shared/useLessonBatchExecution';
import { EmptyStageNotice } from '../../shared/EmptyStageNotice';

function collectExerciseSpecs(lessonContent: any): Array<{ exercise_id: string; section_title: string; intent: string; context_from_text: string }> {
  const specs: any[] = [];
  for (const section of lessonContent?.sections || []) {
    for (const item of section.content || []) {
      if (item.type === 'exercise') {
        specs.push({
          exercise_id: `ex_${specs.length}`,
          section_title: section.title || '(Opening)',
          intent: item.intent || '',
          context_from_text: item.context_from_text || '',
        });
      }
    }
  }
  return specs;
}

export function LessonExerciseReview() {
  const { document, lessonRun, refreshDocument, refreshRun } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const specs = collectExerciseSpecs(lessonContent);
  const isApproved = (lessonRun?.stages || []).some(
    (s) => s.stage_id === 'lesson_exercise_review' && s.status === 'succeeded'
  );

  const [draft, setDraft] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const exercises: Record<string, any> = (document?.exercises as any) || {};

  // For retrigger
  const { runSingle, runningSingle, batchStatus } = useLessonBatchExecution({ stageId: 'lesson_exercise_gen' });

  useEffect(() => {
    if (exercises && Object.keys(exercises).length > 0) {
      setDraft(JSON.parse(JSON.stringify(exercises)));
    }
  }, [JSON.stringify(exercises)]);

  const isDirty = Object.keys(draft).length > 0 && JSON.stringify(draft) !== JSON.stringify(exercises);

  const handleSave = useCallback(async () => {
    if (!lessonRun?.run_id) return;
    setSaving(true);
    setSaveError(null);
    setSavedMessage(null);
    try {
      await courseRunsService.updateLessonDocument(lessonRun.run_id, { exercises: draft });
      await refreshDocument();
      setSavedMessage('Saved');
      setTimeout(() => setSavedMessage(null), 3000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [lessonRun?.run_id, draft, refreshDocument]);

  const handleDiscard = () => {
    setDraft(JSON.parse(JSON.stringify(exercises)));
    setSaveError(null);
    setSavedMessage(null);
  };

  const handleApprove = useCallback(async () => {
    if (!lessonRun?.run_id) return;
    setApproving(true);
    setApproveError(null);
    try {
      await courseRunsService.approveLessonStage(lessonRun.run_id, 'lesson_exercise_review');
      await refreshRun();
    } catch (e: unknown) {
      setApproveError(e instanceof Error ? e.message : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  }, [lessonRun?.run_id, refreshRun]);

  if (specs.length === 0 && (document?.lesson_content as any)?.sections?.length) {
    return <EmptyStageNotice stageId="lesson_exercise_review" contentType="exercises" />;
  }

  if (specs.length === 0) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        No exercise specs found. Generate the lesson content first.
      </div>
    );
  }

  if (Object.keys(exercises).length === 0) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        No exercises generated yet. Run the Exercise Generation stage first.
      </div>
    );
  }

  const updateExercise = (exId: string, updated: any) => {
    setDraft({ ...draft, [exId]: updated });
  };

  return (
    <div className="space-y-6 pb-16">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Exercise Review</h3>
            <p className="text-xs text-gray-500">{specs.length} exercises to review</p>
          </div>
          <div className="flex items-center gap-3">
            {savedMessage && <span className="text-xs text-green-600">{savedMessage}</span>}
            {saveError && <span className="text-xs text-red-600">{saveError}</span>}
            <button type="button" className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40" disabled={!isDirty} onClick={handleDiscard}>Discard</button>
            <button type="button" className="px-4 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: '#1a2332' }} disabled={saving || !isDirty} onClick={handleSave}>{saving ? 'Saving...' : 'Save Changes'}</button>
            {!isApproved && !isDirty && (
              <button type="button" className="px-4 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-40" disabled={approving || isDirty} onClick={handleApprove}>{approving ? 'Approving...' : 'Approve Exercises'}</button>
            )}
          </div>
        </div>
      </div>

      {isApproved && (
        <div className="bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Exercises approved.
        </div>
      )}
      {approveError && <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">{approveError}</div>}

      {/* Exercise cards */}
      {specs.map((spec) => {
        const ex = draft[spec.exercise_id];
        const isSelected = selectedId === spec.exercise_id;
        const batch = batchStatus[spec.exercise_id];

        return (
          <div key={spec.exercise_id} className={`border rounded-lg overflow-hidden transition-colors ${isSelected ? 'border-blue-200' : 'border-gray-200'}`}>
            {/* Header — spec + exercise summary */}
            <div
              className="flex items-start gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50"
              onClick={() => setSelectedId(isSelected ? null : spec.exercise_id)}
            >
              {/* Spec (what was requested) */}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase text-gray-400 mb-0.5">Spec</div>
                <div className="text-xs font-medium text-gray-700">{spec.section_title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{spec.intent}</div>
              </div>
              {/* Divider */}
              <div className="w-px bg-gray-200 self-stretch" />
              {/* Generated (what was produced) */}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase text-gray-400 mb-0.5">Generated</div>
                {ex ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">{ex.exercise_type}</span>
                      <span className="text-xs font-medium text-gray-800 truncate">{ex.name}</span>
                    </div>
                    {ex.skill_tags && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ex.skill_tags.map((tag: SkillTag, i: number) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">{skillTagLabel(tag)}</span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-gray-400 italic">Not generated</div>
                )}
              </div>
              {/* Retrigger + chevron */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {ex && (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-blue-600"
                    onClick={(e) => { e.stopPropagation(); runSingle(spec.exercise_id); }}
                    disabled={runningSingle}
                  >{batch?.status === 'running' ? 'Generating...' : 'Regenerate'}</button>
                )}
                <svg className={`w-4 h-4 text-gray-300 transition-transform ${isSelected ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expanded editor */}
            {isSelected && ex && (
              <div className="px-4 py-4 border-t border-gray-100 space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                    <input className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value={ex.name || ''} onChange={(e) => updateExercise(spec.exercise_id, { ...ex, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm" value={ex.exercise_type || ''} onChange={(e) => updateExercise(spec.exercise_id, { ...ex, exercise_type: e.target.value })}>
                      <option value="multiple-choice">multiple-choice</option>
                      <option value="free-form">free-form</option>
                      <option value="missing-words">missing-words</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                  <input className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value={ex.description || ''} onChange={(e) => updateExercise(spec.exercise_id, { ...ex, description: e.target.value })} />
                </div>

                {/* Skill tags */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Skill Tags</label>
                  <div className="flex flex-wrap gap-1">
                    {(ex.skill_tags || []).length === 0 ? (
                      <span className="text-xs text-gray-400 italic">None (assigned from the skill framework + inherited course category)</span>
                    ) : (
                      (ex.skill_tags || []).map((tag: SkillTag, i: number) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-0.5 rounded-full border ${
                            typeof tag === 'object' && tag?.custom
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-blue-50 text-blue-600 border-blue-100'
                          }`}
                        >
                          {skillTagLabel(tag)}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Content blocks */}
                {(ex.content_blocks || []).map((block: any, bIdx: number) => (
                  <div key={bIdx}>
                    {block.type === 'text' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Question / Prompt</label>
                        <textarea
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
                          rows={3}
                          value={block.value || ''}
                          onChange={(e) => {
                            const blocks = [...ex.content_blocks];
                            blocks[bIdx] = { ...blocks[bIdx], value: e.target.value };
                            updateExercise(spec.exercise_id, { ...ex, content_blocks: blocks });
                          }}
                        />
                      </div>
                    )}
                    {block.type === 'answer_list' && (
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-500">Answers</label>
                        {(block.answers || []).map((answer: any, aIdx: number) => (
                          <div key={aIdx} className={`border rounded-md p-3 space-y-2 ${
                            answer.type === 'correct' ? 'border-green-300 bg-green-50/50' :
                            answer.type === 'answer_key' ? 'border-blue-300 bg-blue-50/50' :
                            'border-gray-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              <select
                                className="border border-gray-300 rounded px-2 py-1 text-xs"
                                value={answer.type}
                                onChange={(e) => {
                                  const blocks = [...ex.content_blocks];
                                  const answers = [...blocks[bIdx].answers];
                                  answers[aIdx] = { ...answers[aIdx], type: e.target.value };
                                  blocks[bIdx] = { ...blocks[bIdx], answers };
                                  updateExercise(spec.exercise_id, { ...ex, content_blocks: blocks });
                                }}
                              >
                                <option value="correct">Correct</option>
                                <option value="option">Distractor</option>
                                <option value="answer_key">Answer Key</option>
                              </select>
                              <span className="text-[10px] text-gray-400">{answer.type === 'correct' ? '✓ Correct answer' : answer.type === 'answer_key' ? '★ Model answer' : '○ Distractor'}</span>
                            </div>
                            <textarea
                              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm resize-y"
                              rows={2}
                              value={answer.text || ''}
                              placeholder="Answer text..."
                              onChange={(e) => {
                                const blocks = [...ex.content_blocks];
                                const answers = [...blocks[bIdx].answers];
                                answers[aIdx] = { ...answers[aIdx], text: e.target.value };
                                blocks[bIdx] = { ...blocks[bIdx], answers };
                                updateExercise(spec.exercise_id, { ...ex, content_blocks: blocks });
                              }}
                            />
                            {(answer.type === 'correct' || answer.type === 'option') && (
                              <textarea
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-600 italic resize-y"
                                rows={2}
                                value={answer.feedback || ''}
                                placeholder="Feedback shown to learner..."
                                onChange={(e) => {
                                  const blocks = [...ex.content_blocks];
                                  const answers = [...blocks[bIdx].answers];
                                  answers[aIdx] = { ...answers[aIdx], feedback: e.target.value };
                                  blocks[bIdx] = { ...blocks[bIdx], answers };
                                  updateExercise(spec.exercise_id, { ...ex, content_blocks: blocks });
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* GPT hint for free-form */}
                {ex.exercise_type === 'free-form' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">AI Grading Hint</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs font-mono resize-y"
                      rows={3}
                      value={ex.gpt_hint || ''}
                      onChange={(e) => updateExercise(spec.exercise_id, { ...ex, gpt_hint: e.target.value })}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
